import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiKey, FiX, FiSave, FiExternalLink } = FiIcons;

const KieApiKeyModal = ({ isOpen, onClose, onSave, currentKey }) => {
  const [apiKey, setApiKey] = useState(currentKey || '');
  const [showKey, setShowKey] = useState(false);

  const handleSave = () => {
    if (apiKey.trim()) {
      onSave(apiKey.trim());
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative bg-gray-800/95 backdrop-blur-xl rounded-2xl p-6 w-full max-w-md border border-purple-400/20 shadow-[0_0_50px_rgba(168,85,247,0.1)]"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiKey} className="text-2xl text-purple-400" />
                <h2 className="text-xl font-semibold text-white">Kie.AI API Key</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-purple-400 text-sm font-medium mb-3">
                Enter your Kie.AI API Key
              </label>
              <div className="relative">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="kie_xxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 transition-all duration-300"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-400 transition-colors"
                >
                  {showKey ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              <div className="mt-4 space-y-2">
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span>Your API key is stored securely in your browser's local storage</span>
                </p>
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span>It will persist until you clear your browser data</span>
                </p>
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span>Your key is never sent to any external servers except Kie.AI</span>
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-blue-500/10 border border-blue-400/30 rounded-lg">
              <p className="text-blue-300 text-sm mb-2">
                Don't have an API key yet?
              </p>
              <a
                href="https://kie.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm"
              >
                Get your API key from Kie.AI
                <SafeIcon icon={FiExternalLink} className="text-xs" />
              </a>
            </div>

            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                disabled={!apiKey.trim()}
                className={`flex-1 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                  apiKey.trim()
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-400 hover:to-pink-500 shadow-[0_0_20px_rgba(168,85,247,0.3)]'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                <SafeIcon icon={FiSave} />
                Save API Key
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors duration-300"
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default KieApiKeyModal;